// University of Delaware: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the headline class is now Fall 2025 from
// UD's Facts & Figures 2025-2026, the class the KB holds; residency stays
// the Fall 2024 Common Data Set's.
//  - UD's Common Data Set 2024-2025 (CDS2425_UDelaware.pdf), the newest
//    edition Institutional Research and Effectiveness posts on its Common
//    Data Set page (no 2025-2026 file was posted on 2026-10-01). The file is
//    served from a WordPress CDN host, so the SOURCES url is the ire.udel.edu
//    page that links it. C1 applicants, admits and enrollees for Fall 2024
//    with the residency table; C8 test policy; C21/C22 plans. Quirk: the C1
//    gender rows sum to 39,741 applied and 27,516 admitted, one fewer than
//    the residency table's totals (39,742 and 27,517), whose unknown-gender
//    row is blank; the page uses the CDS totals. The CDS's G1 costs are for
//    2025-2026, so they are not used.
//  - UD Undergraduate Admissions: Freshmen Admissions (timeline, test
//    optional FAQ, precalculus majors, Honors College, nursing and School of
//    Music dates). The page carries no year; it is the page as posted for the
//    application that opened August 1, 2026.
//  - UD Student Financial Services, Cost of Attendance, 2026-2027
//    Undergraduate Newark Campus Rates (DE resident and non-resident, on
//    campus). Its items sum exactly to its totals.
//  - College Scorecard, school id 130943 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UD Common Data Set 2024-2025',
        url: 'https://ire.udel.edu/common-data-set/',
        read: '2026-10-01',
    },
    freshman: {
        label: 'UD Undergraduate Admissions, Freshmen Admissions',
        url: 'https://www.udel.edu/apply/undergraduate-admissions/apply-to-ud/freshman-admissions/',
        read: '2026-10-01',
    },
    costs: {
        label: 'UD Student Financial Services, Cost of Attendance',
        url: 'https://www.udel.edu/students/student-financial-services/coa/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?130943-University-of-Delaware',
        read: '2026-10-15',
    },
    facts: {
        label: 'UD Facts & Figures 2025-2026, First-Year Admissions',
        url: 'https://ire.udel.edu/files/2025/10/09-admis_2025-26.pdf',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'university-of-delaware',
    kbId: 'university_of_delaware',
    name: 'University of Delaware',
    shortName: 'Delaware',
    aliases: ['UDel'],
    location: 'Newark, DE',
    control: 'Public',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (Blue Hens athletics colors page); decoration only.
    branding: {
        primary: '#00539F',
        secondary: '#FFD200',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_delaware_logo.png',
    },

    // The direct answer and short FAQ (#4229, founder 2026-10-14: content
    // only, no title change). Every figure restates one the page already
    // shows from the source named beside it; CollegePages.test.jsx fails a
    // number the rest of the page does not hold.
    // The search it answers: university of delaware application deadline.
    faq: {
        answer: { text: 'For fall 2027 entry, the University of Delaware\'s early action deadline is November 1 and its regular admission priority deadline is January 15, with early action decisions by January 31 and regular decisions by mid-March.', source: 'freshman' },
        questions: [
            { q: 'When is the University of Delaware early action deadline?', a: 'November 1, which is also the Honors College deadline. All early action decisions are released by January 31. Applications sent after 11:59 p.m. on November 1 are read as space allows.', source: 'freshman' },
            { q: 'What is the University of Delaware acceptance rate?', a: 'For fall 2025, UD\'s Facts & Figures counts 40,943 first-year applicants to the Newark campus and 29,745 admitted to Newark, including 128 admitted from the Associate in Arts pool: 72.6%.', source: 'facts', where: 'First-time First-year Admissions Activity, Fall 2025' },
            { q: 'What is the UD acceptance rate for out-of-state students?', a: 'For fall 2024, the latest class UD\'s Common Data Set splits by residency, UD admitted 23,364 of 33,360 out-of-state applicants, 70%. For Delaware residents it was 2,913 of 4,119, 70.7%.', source: 'cds', where: 'section C1, residency table' },
            { q: 'What is the University of Delaware application fee?', a: 'The application fee is $75. The application opens August 1.', source: 'freshman' },
            { q: 'Does UD require the SAT or ACT?', a: 'No. UD is test-optional: you choose on the application whether your scores are considered. If you send them, UD superscores the SAT and takes your highest ACT composite.', source: 'freshman' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: null,
        applied: 40943,
        admitted: 29745,
        enrolled: 4585,
        source: 'facts',
        where: 'Facts & Figures 2025-2026, First-time First-year Admissions Activity, Fall 2025: the 40,943 who applied to the Newark campus; 29,617 of them admitted to Newark plus 128 admitted to Newark from the Associate in Arts pool, and the 4,568 + 17 who enrolled (the Common Data Set\'s Newark definition)',
        residency: [
            { group: 'In-state', applied: 4119, admitted: 2913 },
            { group: 'Out-of-state', applied: 33360, admitted: 23364 },
            { group: 'International', applied: 2263, admitted: 1240 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
        residencyEntering: 'Fall 2024',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UD\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UD has no early decision plan. Its early action plan is not restrictive, and the Common Data Set does not report how many applied or were admitted early, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to UD with a major in mind. Some programs ask for more:',
        rows: [
            { school: 'Nursing', requirement: 'A December 1 deadline: completed applications and materials must be in by 11:59 p.m. that day. Nursing decisions come by February 15.' },
            { school: 'School of Music', requirement: 'An audition. Admissions emails you the audition request form, so apply early for the widest choice of dates. The priority deadline is December 1.' },
            { school: 'Engineering, computer science and most business majors', requirement: 'Precalculus, taken and passed in high school by graduation. Without it you are not considered for the major. Entrepreneurship, Economics (B.A.), Hospitality and Event Experience Management and Sport Management do not need it.' },
            { school: 'Honors College', requirement: 'Apply early action and send every application material by November 1. Every early action applicant is considered, and invitations come in late January.' },
        ],
        note: 'If space runs out in your major, or your application does not meet its requirements, UD may admit you to University Studies, its undeclared major.',
        source: 'freshman',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular admission priority deadline', value: 'January 15' },
        rows: [
            { plan: 'Early action', deadline: 'November 1', decision: 'By January 31', binding: false },
            { plan: 'Nursing', deadline: 'December 1', decision: 'By February 15', binding: false },
            { plan: 'Regular admission (priority)', deadline: 'January 15', decision: 'By mid-March', binding: false },
        ],
        note: 'The application opens August 1 and the fee is $75. November 1 is also the Honors College deadline; it does not apply to nursing or School of Music applicants, whose priority deadline is December 1. Applications sent after 11:59 p.m. on November 1 are read as space allows. To be reviewed for merit awards, apply by January 15 (by November 1 for the Honors College). UD does not interview.',
        source: 'freshman',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UD is test-optional: you choose on the application whether your SAT or ACT scores are considered, and leaving them out does not count against you. If you send them, UD superscores the SAT and takes your highest ACT composite. You can enter your own scores on the application instead of sending official reports. Merit awards and the Honors College are open with or without scores.',
        source: 'freshman',
        cdsLine: 'The 2024-2025 Common Data Set also marks the SAT or ACT as not required, but considered if submitted, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [15740, 42470] },
            { item: 'Mandatory fees', amounts: [2550, 2550] },
            { item: 'Housing (standard, on campus)', amounts: [9286, 9286] },
            { item: 'Food (standard meal plan)', amounts: [7328, 7328] },
            { item: 'Books, supplies, course materials and equipment', amounts: [1250, 1250] },
            { item: 'Personal expenses', amounts: [1942, 1942] },
            { item: 'Transportation', amounts: [1000, 1000] },
            { item: 'Loan fees', amounts: [58, 58] },
        ],
        totals: [39154, 65884],
        note: 'For a full-time undergraduate on the Newark campus, fall and spring. Business, engineering, music, honors and nursing students pay an added differential charge.',
        source: 'costs',
        netPrice: {
            average: 16337,
            byIncome: [
                { income: '$0 to $30,000', amount: 5566 },
                { income: '$30,001 to $48,000', amount: 10783 },
                { income: '$48,001 to $75,000', amount: 13801 },
                { income: '$75,001 to $110,000', amount: 19669 },
                { income: 'Over $110,000', amount: 23862 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UD\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
